<script setup>
// NOTICE: KAPTION-INTEGRATION
// security-center.UI.8 — "Asignación de roles" (canvas: Provisioning): IdP
// group → Rumo role (+ teams), what happens to people in no mapped group, and
// which claim carries the groups. The server keeps only this account's teams.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import KaptionSecurityAPI from 'dashboard/api/kaptionSecurity';
import { useKaptionSecurity } from 'dashboard/composables/useKaptionSecurity';
import { useKaptionSecurityText } from '../useKaptionSecurityText';

const { t } = useI18n();
const store = useStore();
const teams = useMapGetter('teams/getTeams');
const { overview, setOverview, settings, connection } = useKaptionSecurity();
const { provider } = useKaptionSecurityText(overview);

const dialogRef = ref(null);
const rows = ref([]);
const unmapped = ref('agent');
const claim = ref('groups');
const busy = ref(false);
const error = ref('');

const open = () => {
  store.dispatch('teams/get');
  rows.value = (settings.value.group_mappings || []).map(row => ({
    group: row.group,
    role: row.role,
    team_ids: [...(row.team_ids || [])],
  }));
  if (!rows.value.length)
    rows.value.push({ group: '', role: 'agent', team_ids: [] });
  unmapped.value = settings.value.unmapped_action || 'agent';
  claim.value = settings.value.groups_claim || 'groups';
  error.value = '';
  dialogRef.value?.open();
};

const addRow = () =>
  rows.value.push({ group: '', role: 'agent', team_ids: [] });
const removeRow = index => rows.value.splice(index, 1);

const isAuth0 = computed(() => connection.value?.provider === 'auth0');

const save = async () => {
  busy.value = true;
  error.value = '';
  try {
    const { data } = await KaptionSecurityAPI.updateProvisioning({
      unmapped_action: unmapped.value,
      groups_claim: claim.value.trim() || 'groups',
      group_mappings: rows.value
        .filter(row => row.group.trim())
        .map(row => ({
          group: row.group.trim(),
          role: row.role,
          team_ids: row.team_ids,
        })),
    });
    setOverview(data);
    useAlert(t('KAPTION_SECURITY.COMMON.SAVED'));
    dialogRef.value?.close();
  } catch (e) {
    error.value =
      e?.response?.data?.error || t('KAPTION_SECURITY.COMMON.ERROR');
  } finally {
    busy.value = false;
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('KAPTION_SECURITY.USERS.MAPPING_TITLE')"
    :description="t('KAPTION_SECURITY.USERS.MAPPING_DESC', { provider })"
    :confirm-button-label="t('KAPTION_SECURITY.COMMON.SAVE')"
    :is-loading="busy"
    width="3xl"
    overflow-y-auto
    @confirm="save"
  >
    <div class="flex flex-col gap-5">
      <div class="border rounded-xl border-n-weak">
        <div
          class="hidden sm:grid grid-cols-[minmax(0,1fr)_150px_minmax(0,1fr)_32px] gap-3 px-4 py-2.5 text-xs font-medium uppercase text-n-slate-11 bg-n-alpha-1 rounded-t-xl"
        >
          <span>{{
            t('KAPTION_SECURITY.USERS.HEAD_GROUP', { provider })
          }}</span>
          <span>{{ t('KAPTION_SECURITY.USERS.HEAD_ROLE') }}</span>
          <span>{{ t('KAPTION_SECURITY.USERS.HEAD_TEAMS') }}</span>
          <span />
        </div>
        <div
          v-for="(row, index) in rows"
          :key="index"
          class="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_150px_minmax(0,1fr)_32px] gap-3 px-4 py-3 border-t border-n-weak items-start"
          :data-testid="`ks-mapping-row-${index}`"
        >
          <input
            v-model="row.group"
            type="text"
            class="h-9 px-3 font-mono text-[13px] border rounded-lg border-n-weak bg-n-alpha-black2 text-n-slate-12"
            :placeholder="t('KAPTION_SECURITY.USERS.GROUP_PLACEHOLDER')"
            :aria-label="t('KAPTION_SECURITY.USERS.HEAD_GROUP', { provider })"
          />
          <select
            v-model="row.role"
            class="h-9 px-2 text-sm border rounded-lg border-n-weak bg-n-alpha-black2 text-n-slate-12"
            :aria-label="
              t('KAPTION_SECURITY.USERS.ROLE_FOR', { group: row.group })
            "
          >
            <option value="administrator">
              {{ t('KAPTION_SECURITY.USERS.ADMINISTRATOR') }}
            </option>
            <option value="agent">
              {{ t('KAPTION_SECURITY.USERS.AGENT') }}
            </option>
          </select>
          <select
            v-model="row.team_ids"
            multiple
            class="px-2 py-1 text-sm border rounded-lg min-h-9 border-n-weak bg-n-alpha-black2 text-n-slate-12"
            :aria-label="
              t('KAPTION_SECURITY.USERS.TEAMS_FOR', { group: row.group })
            "
          >
            <option v-for="team in teams" :key="team.id" :value="team.id">
              {{ team.name }}
            </option>
          </select>
          <button
            type="button"
            class="flex items-center justify-center rounded-md size-8 text-n-slate-11 hover:bg-n-alpha-2"
            :aria-label="
              t('KAPTION_SECURITY.USERS.REMOVE_GROUP', { group: row.group })
            "
            @click="removeRow(index)"
          >
            <span class="i-lucide-x size-4" aria-hidden="true" />
          </button>
        </div>
        <div class="px-4 py-3 border-t border-n-weak">
          <button
            type="button"
            class="text-sm font-medium text-n-brand"
            @click="addRow"
          >
            {{ t('KAPTION_SECURITY.USERS.ADD_GROUP') }}
          </button>
        </div>
      </div>

      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div class="flex flex-col gap-1.5">
          <label
            for="ks-unmapped"
            class="text-sm font-medium text-n-slate-12"
            >{{ t('KAPTION_SECURITY.USERS.UNMAPPED_LABEL') }}</label
          >
          <select
            id="ks-unmapped"
            v-model="unmapped"
            class="h-10 px-3 text-sm border rounded-xl border-n-weak bg-n-alpha-black2 text-n-slate-12"
          >
            <option value="agent">
              {{ t('KAPTION_SECURITY.USERS.UNMAPPED_AGENT') }}
            </option>
            <option value="deny">
              {{ t('KAPTION_SECURITY.USERS.UNMAPPED_DENY') }}
            </option>
          </select>
        </div>
        <div class="flex flex-col gap-1.5">
          <label for="ks-claim" class="text-sm font-medium text-n-slate-12">{{
            t('KAPTION_SECURITY.USERS.CLAIM_LABEL', { provider })
          }}</label>
          <input
            id="ks-claim"
            v-model="claim"
            type="text"
            class="h-10 px-3 font-mono text-[13px] border rounded-xl border-n-weak bg-n-alpha-black2 text-n-slate-12"
          />
        </div>
      </div>

      <div
        class="flex gap-2.5 px-3 py-2.5 text-sm rounded-lg bg-n-amber-3 text-n-amber-11"
      >
        <span class="mt-0.5 i-lucide-info size-4 shrink-0" aria-hidden="true" />
        <span>{{
          isAuth0
            ? t('KAPTION_SECURITY.USERS.NOTE_AUTH0')
            : t('KAPTION_SECURITY.USERS.NOTE_GENERIC', { provider })
        }}</span>
      </div>
      <p
        v-if="error"
        class="px-3 py-2 m-0 text-sm rounded-lg bg-n-ruby-3 text-n-ruby-11"
      >
        {{ error }}
      </p>
    </div>
  </Dialog>
</template>
